import { useState, useEffect } from 'react'
import { X } from '@/ui/icons'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Textarea } from '@/ui/components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/components'
import { FormField, FormSection, FormHeader } from '@/ui/components'
import { VehicleProfile, UpdateVehicleProfileDto } from '@/shared/api/vehicles'
import { VehicleApi } from '@/shared/api/vehicles'
import { useAuth } from '@/modules/auth'

interface VehicleEditModalProps {
  isOpen: boolean
  onClose: () => void
  vehicle: VehicleProfile
  onVehicleUpdated: (vehicle: VehicleProfile) => void
}

export function VehicleEditModal({
  isOpen,
  onClose,
  vehicle,
  onVehicleUpdated
}: VehicleEditModalProps) {
  const { getAccessToken } = useAuth()
  const vehicleApi = new VehicleApi(getAccessToken)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState<UpdateVehicleProfileDto>({
    name: vehicle.name || '',
    vehicle_type: vehicle.vehicle_type,
    make: vehicle.make,
    model: vehicle.model,
    year: vehicle.year,
    notes: vehicle.notes || '',
    is_daily_driver: vehicle.is_daily_driver || false,
    has_winch: vehicle.has_winch || false,
    has_skid_plates: vehicle.has_skid_plates || false,
    has_rock_sliders: vehicle.has_rock_sliders || false,
    has_roof_rack: vehicle.has_roof_rack || false,
    has_roof_top_tent: vehicle.has_roof_top_tent || false,
    has_truck_camper: vehicle.has_truck_camper || false
  })

  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle.name || '',
        vehicle_type: vehicle.vehicle_type,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        notes: vehicle.notes || '',
        is_daily_driver: vehicle.is_daily_driver || false,
        has_winch: vehicle.has_winch || false,
        has_skid_plates: vehicle.has_skid_plates || false,
        has_rock_sliders: vehicle.has_rock_sliders || false,
        has_roof_rack: vehicle.has_roof_rack || false,
        has_roof_top_tent: vehicle.has_roof_top_tent || false,
        has_truck_camper: vehicle.has_truck_camper || false
      })
      setError(null)
    }
  }, [vehicle])

  const handleInputChange = (field: keyof UpdateVehicleProfileDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.make?.trim()) {
      setError('Make is required')
      return
    }
    if (!formData.model?.trim()) {
      setError('Model is required')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const updateData: UpdateVehicleProfileDto = {
        ...formData,
        name: formData.name?.trim() || null
      }

      const updated = await vehicleApi.updateVehicle(vehicle.id, updateData)
      onVehicleUpdated(updated)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update vehicle')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-earth-200 shadow-lg">
        <div className="sticky top-0 bg-card border-b border-earth-200 px-6 py-4 flex items-center justify-between">
          <FormHeader
            eyebrow="Your vehicle"
            title="Tell us about your rig"
            className="flex-1 mr-4"
          />
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isLoading} className="shrink-0 self-start mt-1">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          <FormSection title="Vehicle details">
            <FormField
              label="Nickname"
              htmlFor="name"
              hint="Optional — if blank, we'll use make and model."
            >
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Desert Fox, Weekend Warrior"
              />
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Make" htmlFor="make" required>
                <Input
                  id="make"
                  value={formData.make || ''}
                  onChange={(e) => handleInputChange('make', e.target.value)}
                  placeholder="e.g., Toyota"
                  required
                />
              </FormField>
              <FormField label="Model" htmlFor="model" required>
                <Input
                  id="model"
                  value={formData.model || ''}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  placeholder="e.g., Tacoma"
                  required
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Year" htmlFor="year">
                <Input
                  id="year"
                  type="number"
                  value={formData.year || ''}
                  onChange={(e) => handleInputChange('year', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={1900}
                  max={new Date().getFullYear() + 1}
                />
              </FormField>
              <FormField label="Vehicle type" htmlFor="vehicle_type">
                <Select
                  value={formData.vehicle_type}
                  onValueChange={(value) => handleInputChange('vehicle_type', value)}
                >
                  <SelectTrigger id="vehicle_type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="suv">SUV</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <div className="flex items-center space-x-2 pt-1">
              <input
                id="is_daily_driver"
                type="checkbox"
                checked={!!formData.is_daily_driver}
                onChange={(e) => handleInputChange('is_daily_driver', e.target.checked)}
                className="rounded border-earth-200 text-adventure-500 focus:ring-adventure-500"
              />
              <label htmlFor="is_daily_driver" className="text-sm font-medium text-foreground cursor-pointer">
                Daily driver
              </label>
            </div>

            <FormField label="Notes" htmlFor="notes">
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Mods, quirks, maintenance notes..."
                rows={3}
              />
            </FormField>
          </FormSection>

          <FormSection title="Equipment" description="Check everything that's currently on your rig.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { field: 'has_winch', label: 'Winch' },
                { field: 'has_skid_plates', label: 'Skid plates' },
                { field: 'has_rock_sliders', label: 'Rock sliders' },
                { field: 'has_roof_rack', label: 'Roof rack' },
                { field: 'has_roof_top_tent', label: 'Roof top tent' },
                { field: 'has_truck_camper', label: 'Truck camper' },
              ].map(({ field, label }) => (
                <label key={field} className="flex items-center gap-3 text-sm font-medium text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData[field as keyof UpdateVehicleProfileDto]}
                    onChange={(e) => handleInputChange(field as keyof UpdateVehicleProfileDto, e.target.checked)}
                    className="rounded border-earth-200 text-adventure-500 focus:ring-adventure-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </FormSection>

          {error && (
            <div className="bg-error/10 border border-error/30 rounded-md p-3">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-2 border-t border-earth-200">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save vehicle'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
